/**
 * 404 — role-aware, in the court-exhibit grammar. The page that can't be found
 * is framed as the thing this platform exists to prevent: a figure that doesn't
 * trace to a source.
 *   • Attorneys → the "objection" variant: evidence that never made it into the record.
 *   • The number seats (CPA internal/external, data specialist) → the "reconciliation"
 *     variant: a ledger that won't tie out — the "404" is the unreconciled variance.
 *   • EVERYONE ELSE — signed-out visitors (the bulk of 404 traffic), clients, and
 *     staff — gets the "neutral" variant: the same exhibit grammar, no seat-specific
 *     jargon. A logged-out stranger on a stale link must not be shown CPA-speak.
 * All three variants are exported so the harness can screenshot each without a session.
 */

import { useLocation } from 'react-router-dom';
import PublicShell from '../components/marketing/PublicShell';
import { PrimaryCTA, SectionLink } from '../components/home/sectionPrimitives';
import useWorkspaceRole from '../hooks/useWorkspaceRole';
import type { WorkspaceRole } from '../config/toolsManifest';

const HAIR = { borderColor: 'var(--color-hairline)' } as const;
/** Textbook close: single rule above the total, double rule below it. */
const TOTAL_RULE = {
  borderTop: '1px solid var(--color-text-dim)',
  borderBottom: '3px double var(--color-text-dim)',
} as const;

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <section className="mx-auto flex min-h-[60vh] max-w-2xl flex-col items-center justify-center px-5 py-24">
      {children}
    </section>
  );
}

function Actions() {
  return (
    <div className="mt-10 flex flex-wrap items-center justify-center gap-6">
      <PrimaryCTA to="/">Back to homepage</PrimaryCTA>
      <SectionLink to="/sign-in">Sign in</SectionLink>
    </div>
  );
}

/** Shared ruled exhibit panel — header (path + status), ledger body, optional footer strip. */
function Exhibit({
  path,
  status,
  children,
  footer,
}: {
  path: string;
  status: string;
  children: React.ReactNode;
  footer?: string;
}) {
  return (
    <div className="mt-10 w-full border bg-surface" style={HAIR}>
      <div
        className="flex items-center justify-between gap-4 border-b px-5 py-3 font-mono text-[10px] uppercase tracking-[0.16em] text-text-dim"
        style={HAIR}
      >
        <span className="truncate">Exhibit · {path}</span>
        <span className="shrink-0">{status}</span>
      </div>
      <div className="px-5 py-5 sm:px-6">{children}</div>
      {footer && (
        <p
          className="border-t px-5 py-3 font-mono text-[10px] uppercase tracking-[0.14em] text-text-dim sm:px-6"
          style={HAIR}
        >
          {footer}
        </p>
      )}
    </div>
  );
}

/** One register line — label left, figure right in DM Mono tabular. */
function LedgerRow({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2">
      <span className={`font-mono text-sm ${muted ? 'text-text-muted' : 'text-text-secondary'}`}>{label}</span>
      <span className={`font-mono text-sm tabular-nums ${muted ? 'text-text-muted' : 'text-text-primary'}`}>
        {value}
      </span>
    </div>
  );
}

/** The total row — the 404 is the figure that won't resolve. Rule above, double rule below. */
function TotalRow({ label }: { label: string }) {
  return (
    <div className="mt-1.5 flex items-baseline justify-between gap-4 pt-3 pb-4" style={TOTAL_RULE}>
      <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-text-dim">{label}</span>
      <span className="font-heading text-5xl leading-none tracking-tight text-brand-text">404</span>
    </div>
  );
}

function Heading({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <>
      <p className="font-mono text-xs uppercase tracking-[0.22em] text-text-dim">{eyebrow}</p>
      <h1 className="mt-5 text-center font-heading text-4xl leading-tight tracking-tight text-text-primary md:text-5xl">
        {title}
      </h1>
    </>
  );
}

/** Number seats (CPA / data specialist): the 404 as a reconciliation that won't tie out. */
export function NotFoundReconciliation({ path }: { path: string }) {
  return (
    <Frame>
      <Heading eyebrow="Reconciliation · no source" title="This page doesn’t reconcile." />
      <Exhibit path={path} status="No source" footer="Variance 100% · unsupported">
        <LedgerRow label="Pages expected" value="1" />
        <LedgerRow label="Pages found" value="0" muted />
        <TotalRow label="Unreconciled" />
      </Exhibit>
      <p className="mt-8 max-w-md text-center font-body text-sm leading-relaxed text-text-secondary">
        The path you requested doesn’t trace to any page on the record — and we don’t ship a figure
        that can’t be traced.
      </p>
      <Actions />
    </Frame>
  );
}

/** Attorney: the 404 as evidence that was never entered into the record. */
export function NotFoundObjection({ path }: { path: string }) {
  return (
    <Frame>
      <Heading eyebrow="Objection · sustained" title="Not in the record." />
      <Exhibit path={path} status="Stricken" footer="Objection sustained · stricken from the record">
        <LedgerRow label="Pages offered" value="1" />
        <LedgerRow label="Pages admitted" value="0" muted />
        <TotalRow label="Not in evidence" />
      </Exhibit>
      <p className="mt-8 max-w-lg text-center font-body text-sm leading-relaxed text-text-secondary">
        The page you requested was never entered into the record. Nothing is admitted here unless it
        can be sourced.
      </p>
      <Actions />
    </Frame>
  );
}

/** Signed-out visitors, clients, and staff: the exhibit grammar without seat-specific jargon. */
export function NotFoundNeutral({ path }: { path: string }) {
  return (
    <Frame>
      <Heading eyebrow="Not found · 404" title="Wait — something didn’t add up." />
      <Exhibit path={path} status="No match">
        <LedgerRow label="Expected" value="1" />
        <LedgerRow label="Found" value="0" muted />
        <TotalRow label="Doesn’t add up" />
      </Exhibit>
      <p className="mt-8 max-w-md text-center font-body text-sm leading-relaxed text-text-secondary">
        The page you’re looking for doesn’t exist, or it has moved. Check the address, or head back to
        the homepage.
      </p>
      <Actions />
    </Frame>
  );
}

const RECONCILE_ROLES: ReadonlySet<WorkspaceRole> = new Set([
  'accountant_internal',
  'accountant_external',
  'data_specialist',
]);

function variantFor(role: WorkspaceRole | null): 'objection' | 'reconciliation' | 'neutral' {
  if (role === 'attorney') return 'objection';
  if (role && RECONCILE_ROLES.has(role)) return 'reconciliation';
  return 'neutral';
}

export default function NotFound() {
  const role = useWorkspaceRole();
  const { pathname } = useLocation();
  const path = pathname && pathname !== '/' ? pathname : '/—';
  const variant = variantFor(role);

  return (
    <PublicShell wide>
      {variant === 'objection' ? (
        <NotFoundObjection path={path} />
      ) : variant === 'reconciliation' ? (
        <NotFoundReconciliation path={path} />
      ) : (
        <NotFoundNeutral path={path} />
      )}
    </PublicShell>
  );
}
